import { AfterViewInit, Directive, ElementRef, EventEmitter, Inject, Input, OnDestroy, Output } from '@angular/core';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map, pairwise, startWith, tap } from 'rxjs/operators';

interface ScrollPosition {
	sH: number;
	sT: number;
	cH: number;
}

@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: '[luInfiniteScroll]'
})
export class LuInfiniteScrollDirective implements AfterViewInit, OnDestroy {
	@Input() scrollPercent = 80;
	@Input() debounceTime = 300;
	@Output() scrolled = new EventEmitter();
	private scrollEvent$: Observable<any>;
	private scrollSub$: Subscription;

	constructor(@Inject(ElementRef) private el: ElementRef) {}

	ngAfterViewInit(): void {
		this.scrollEvent$ = fromEvent(this.el.nativeElement, 'scroll').pipe(
			startWith(null as Event),
			debounceTime(this.debounceTime),
			map((e: Event): ScrollPosition => {
				if (!!e) {
					return {
						sH: (e.target as HTMLElement).scrollHeight,
						sT: (e.target as HTMLElement).scrollTop,
						cH: (e.target as HTMLElement).clientHeight
					};
				}
				return { sH: 0, sT: 0, cH: 0 };
			}),
			pairwise(),
			filter(
				(positions: Array<ScrollPosition>) =>
					this.isUserScrollingDown(positions) && this.isScrollExpectedPercent(positions[1])
			),
			tap(() => this.scrolled.emit())
		);

		this.scrollSub$ = this.scrollEvent$.subscribe();
	}

	ngOnDestroy(): void {
		if (this.scrollSub$) {
			this.scrollSub$.unsubscribe();
		}
	}

	private isUserScrollingDown = (positions: Array<ScrollPosition>): boolean => {
		return positions[0].sT < positions[1].sT;
	};

	private isScrollExpectedPercent = (position: ScrollPosition): boolean => {
		return (position.sT + position.cH) / position.sH > this.scrollPercent / 100;
	};
}
